/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import { SearchIcon, IconCancel } from "components/icons/common";
import { Button } from "components/ui/Button";
import useDevAttributes from "hooks/useDevAttributes";
import { useRef, useState } from "react";
import s from "./SearchInput.module.css";
import { IVariant } from "components/ui/Button/interface";

interface ISearchInput {
  onSearch: (e: any) => void;
  onToggle?: () => void;
  isLoading?: boolean;
  placeholder?: string;
  variant?: "header" | "page";
  defaultValue?: string;
  className?: string;
  alwaysOpen?: boolean;
}

const SearchInput = ({
  onSearch,
  isLoading,
  placeholder,
  onToggle,
  variant = "page",
  defaultValue = "",
  alwaysOpen = false,
  className,
}: ISearchInput) => {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setOpen] = useState(alwaysOpen);

  const handleSearch = () => {
    onToggle?.();
    // to wait for animation to finish
    setTimeout(() => {
      inputRef.current?.focus();
    }, 150);

    if (isOpen) {
      if (!alwaysOpen) setOpen(false);
      return onSearch(value);
    }

    return setOpen(true);
  };

  let bgTheme: string = "";
  let buttonVariant: IVariant = "secondary";
  if (variant === "page") {
    bgTheme = "bg-primary-600";
    buttonVariant = "secondary";
  }

  if (variant === "header") {
    bgTheme = "bg-secondary";
    buttonVariant = "unstyled";
  }

  const { searchAttr } = useDevAttributes({
    searchAttr: "search-input",
  });

  return (
    <div
      className={cn(
        s.root,
        isOpen ? " w-72 xs:w-64" : "w-12",
        bgTheme,
        className,
      )}
      test-id="O_xNR9JH"
    >
      <div test-id="vLFoIXHH">
        <Button
          {...searchAttr}
          variant={buttonVariant}
          className="px-3 py-2 text-center"
          onClick={handleSearch}
          test-id="RSDzzaUz"
        >
          {isLoading ? (
            <svg
              role="status"
              className="w-[18px] h-[18px] -ml-0.5 animate-spin text-gray-600 fill-gray-300"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              test-id="LUMi_qt5"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
                test-id="CnpjnaPD"
              />

              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
                test-id="dTdWtS4l"
              />
            </svg>
          ) : (
            <SearchIcon test-id="Pyr-ZlM9" />
          )}
        </Button>
      </div>

      <div
        className={cn(
          s.searchContainer,
          isOpen ? "visible duration-300" : "invisible",
          { "mr-2": alwaysOpen },
        )}
        test-id="32KMBj9e"
      >
        <input
          type="text"
          ref={inputRef}
          placeholder={placeholder || "Search..."}
          className={s.searchInput}
          onChange={(e) => setValue(e.target.value)}
          // Add value to empty the input if i delete the value
          value={value}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
            if (e.key === "Escape") setValue("");
          }}
          test-id="G1Fuhkv6"
        />

        {alwaysOpen === false && (
          <div test-id="syzIDuXE">
            <Button
              className="px-3 py-2"
              variant={buttonVariant}
              // Clear input:
              onClick={() => {
                if (isOpen) {
                  onToggle?.();
                }

                setOpen(false);
                setValue("");
              }}
              test-id="PJhaDmUd"
            >
              <IconCancel size="xs" strokeWidth="3" test-id="6RoBRQRG" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchInput;
